import "./Characters.css";
import "./Tabs.css";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ABILITY_SCORES, RpgRules } from "../../Services/RpgRulesService";
import { IRpgCharacter } from "../../models/IRpgCharacter";
import { IRpgRace } from "../../models/IRpgRace";
import { IRpgClass } from "../../models/IRpgClass";
import { IRpgBackground } from "../../models/IRpgBackground";
import AbilitiesTab from "./WorkshopTabs/AbilitiesTab";
import { getCharacterPortrait, getCoinQty, getPointBuyCost, delCharFromStorage, saveCharToStorage, getCharFromStorage } from "./characterHelpers";
import RaceTab from "./WorkshopTabs/RaceTab";
import ClassTab from "./WorkshopTabs/ClassTab";
import BackgroundTab from "./WorkshopTabs/BackgroundTab";
import ReviewTab from "./WorkshopTabs/ReviewTab";
import { canAdjustPointBuy, getRemainingPointBuyPoints, } from "./characterHelpers";
import { ICharacterPortraitProps, ICharacterStatChip } from "../../models/ICharacter";
import { charAPI } from "../../api/charAPI";
import { useAuth } from "../../auth/AuthContext";
import { toastService } from "../../Services/ToastService";
import { ISaveCharacter } from "../../models/ISaveCharacter";
import { IRpgAlignment } from "../../models/IRpgAlignment";
import { logger } from "../../Services/LoggingService";

type TabLock = {
    disabled: boolean;
    title?: string;
};
export const workshopTabs = ["Abilities", "Race", "Class", "Background", "Equipment", "Review"] as const;
export type WorkshopTab = typeof workshopTabs[number];
export type WorkshopMode = "create" | "edit" | "view";

function isStatsReady(char: IRpgCharacter, pointsRemaining: number): boolean {
	// Stats always exist, but "ready" means they're meaningful
	if (!char.AbilityScores || char.AbilityScores.length !== 6) {
		return false;
	}

    return (pointsRemaining < 1);
}

export const CharacterWorkshop: React.FC = () => {
    const { user } = useAuth();
	const { charId } = useParams();
	const navigate = useNavigate();
    const route = useLocation();

    //const [isLoading, setIsLoading] = useState(false);
	const [activeTab, setActiveTab] = useState<WorkshopTab>("Abilities");
    const [hasSaved, setHasSaved] = useState<boolean>(false);

    const [raceList, setRaceList] = useState<IRpgRace[]>([]);
    const [selectedRace, setSelectedRace] = useState<IRpgRace | null>(null);
    const [backgroundList, setBackgroundList] = useState<IRpgBackground[]>([]);
    const [selectedBackground, setSelectedBackground] = useState<IRpgBackground | null>(null);
    const [classList, setClassList] = useState<IRpgClass[]>([]);
    const [selectedClass, setSelectedClass] = useState<IRpgClass | null>(null);
    const [alignmentList, setAlignmentList] = useState<IRpgAlignment[]>([]);

	const [tempCharacter, setTempCharacter] = useState<IRpgCharacter>(() => {
		// 1) Start from default newChar
		const baseChar = RpgRules.newChar();

		// 2) Ensure stats always exist
        // TODO: This should probably be just a step newChar() does in the RpgRulesService
		baseChar.AbilityScores = RpgRules.setDefaultStats(baseChar.AbilityScores);

		return baseChar;
	});

    const pointsRemaining = useMemo(() => { return getRemainingPointBuyPoints(tempCharacter.AbilityScores); }, [tempCharacter.AbilityScores]);
	const statsReady = useMemo(() => isStatsReady(tempCharacter, pointsRemaining), [tempCharacter, pointsRemaining]);
    const reviewIssues = useMemo(() => 
        buildReviewIssues(tempCharacter, statsReady),
        [tempCharacter, statsReady]
    );
    const workshopMode = useMemo<WorkshopMode>(() => {
        const urlPath = route.pathname;

        if (urlPath.includes("/view")) {
            return "view";
        }

        if (urlPath.includes("/edit")) {
            return "edit";
        }

        return "create";
    }, [route.pathname]);
    const isReadOnly = workshopMode === "view";
    const canSave = reviewIssues.length === 0;

    // API calls
    const getAlignmentList = () => {
        //setIsLoading(true);

        charAPI.getAlignments()
        .then((ret: IRpgAlignment[]) => {
            // User should not be able to select "Unknown".
            setAlignmentList(ret.filter((alignment) => alignment.Alignment.toLowerCase() !== "unknown"));
            //setIsLoading(false);
        })
        .catch((err: any) => {
            logger.error("Error making get alignments call:", err);
            //setIsLoading(false);
        });
    };

    // useEffects
    // fetch the character when in view/edit
    useEffect(() => {
        loadChar();
    }, [charId, workshopMode, user.Id]);

	// Persist draft to sessionStorage whenever it changes
	useEffect(() => {
        if (isReadOnly) {
            return;
        }
        saveCharToStorage(tempCharacter);
	}, [tempCharacter, charId, workshopMode]);

    // If the user changes anything after a save, revert actions back to "Save"
    useEffect(() => {
        if (hasSaved) {
            setHasSaved(false);
        }
        // Intentionally only depends on tempCharacter object identity. Any edit that produces a new object should reset hasSaved.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tempCharacter]);

    // Load Alignments
    useEffect(() => {
        getAlignmentList();
    }, []);

    // Load races
    useEffect(() => {
        const races = RpgRules.fakeRaceList();
        setRaceList(races);
    }, []);

    // Sync selected race when tempCharacter.Race OR raceList changes
    useEffect(() => {
        if (!tempCharacter.Race || raceList.length === 0) {
            return;
        }

        const matchingRace = raceList.find((race) => race.Name === tempCharacter.Race);
        if (matchingRace) {
            setSelectedRace(matchingRace);
        }
    }, [tempCharacter.Race, raceList]);

    // Load Classes
    useEffect(() => {
        const classes = RpgRules.fakeClassList();
        setClassList(classes);
    }, []);

    // Sync selected class when tempCharacter.Class OR classList changes
    useEffect(() => {
        if (!tempCharacter.Class || classList.length === 0) {
            return;
        }

        const matchingClass = classList.find((cls) => cls.Name === tempCharacter.Class);
        if (matchingClass) {
            setSelectedClass(matchingClass);
        }
    }, [tempCharacter.Class, classList]);

    // Load backgrounds
    useEffect(() => {
        const backgrounds = RpgRules.fakeBackgroundList();
        setBackgroundList(backgrounds);
    }, []);

    // Sync selected background when tempCharacter.Background OR backgroundList changes
    useEffect(() => {
        if (!tempCharacter.Background || backgroundList.length === 0) {
            return;
        }

        const matchingBackground = backgroundList.find((background) => background.Name === tempCharacter.Background);

        if (matchingBackground) {
            setSelectedBackground(matchingBackground);
        }
    }, [tempCharacter.Background, backgroundList]);

    // Helping methods
    const buildPortraitProps = () => {
        const statChips: ICharacterStatChip[] = [
            { key: "ac", value: tempCharacter.ArmorClass ?? 0, label: "AC" },
            { key: "hp", value: `${tempCharacter.HitPoints ?? 0}/${tempCharacter.HitPointsMax ?? 0}`, label: "HP" },
            { key: "init", value: RpgRules.calcInitive(tempCharacter) ?? 0, label: "INIT" },
        ];

        const ret: ICharacterPortraitProps = {
            layout: "standard",
            size: "lg",
            imageUrl: getCharacterPortrait(tempCharacter),
            level: 1,
            name: tempCharacter.Name,
            subtitle: `${tempCharacter.Race} ${tempCharacter.Class}`,
            stats: statChips,
        };

        return ret;
    };

    function buildReviewIssues(char: IRpgCharacter, statsReady: boolean): string[] {
        const issues: string[] = [];

        if (!statsReady) issues.push("Ability Scores are not finalized.");
        if (!char.Race) issues.push("Race not selected.");
        if (!char.Class) issues.push("Class not selected.");
        if (!char.Name || char.Name.trim().length === 0) issues.push("Character name is required.");
        if (!char.Alignment) issues.push("Alignment not selected.");

        // “Creation can go negative” idea: enforce before save (UI-level)
        const coinNames = ["PP", "EP", "GP", "SP", "CP"];
        coinNames.forEach(coin => {
            const qty = getCoinQty(char, coin);
            if (qty < 0) issues.push(`Coin count cannot be negative: ${coin} is ${qty}.`);
        });

        // No negative Item quantities
        (char.Items ?? []).forEach(item => {
            if ((item.Quantity ?? 0) < 0) issues.push(`Item quantity cannot be negative: ${item.Name || `Item#${item.Id}`} is ${item.Quantity}.`);
        });

        return issues;
    }

    const getTabButtonProps = (tab: WorkshopTab) => {
        const isActive = activeTab === tab;
        const lock = getTabLock(tab);

        return {
            className: `tab${isActive ? " is-active" : ""}`,
            role: "tab" as const,
            "aria-selected": isActive,

            tabIndex: isActive ? 0 : -1,
            disabled: lock.disabled,
            title: lock.title,
            onClick: () => setTabIfAllowed(tab),
        };
    };

    const getTabLock = (tab: WorkshopTab): TabLock => {
        switch (tab) {
            case "Abilities":
                return { disabled: false };

            case "Race":
                return {
                    disabled: !statsReady,
                    title: !statsReady ? "Finish Ability Scores to unlock Race." : undefined,
                };

            case "Class":
                return {
                    disabled: !statsReady,
                    title: !statsReady ? "Finish Ability Scores to unlock Class." : undefined,
                };

            case "Background":
                return {
                    disabled: !statsReady || !tempCharacter.Race || !tempCharacter.Class,
                    title: (!statsReady || !tempCharacter.Race || !tempCharacter.Class) ? "Finish Ability Scores and Select a Race and Class to unlock Background." : undefined,
                };

            case "Review":
                return {
                    disabled: !statsReady || !tempCharacter.Race || !tempCharacter.Class || !tempCharacter.Alignment,
                    title: (!statsReady || !tempCharacter.Race || !tempCharacter.Class || !tempCharacter.Alignment) ? "Finish Ability Scores and Select Race and Class to unlock Review." : undefined,
                };

            default:
                return { disabled: true };
        }
    };

    const loadChar = () => {
        if (!charId || charId === "" || charId === "0") {
            return;
        }
        if (workshopMode === "create") {
            return;
        }
        if (!user?.Id || user.Id <= 0) {
            return;
        }

        setTempCharacter(getCharFromStorage(charId));
    };

	const setTabIfAllowed = (tab: WorkshopTab) => {
		if (tab === "Abilities") {
			setActiveTab(tab);
			return;
		}

        if (!statsReady) {
			return;
		}

		// if ((tab === "Race" || tab === "Class") && !statsReady) {
		// 	return;
		// }

		// Background requires name/race/class etc.
        if (tab === "Background") {
            //if (!statsReady || !tempCharacter.Race || !tempCharacter.Class) {
            if (!tempCharacter.Race || !tempCharacter.Class) {
                return;
            }
            setActiveTab(tab);
            return;
        }

		// Review requires name/race/class/alignment etc.
		if (tab === "Review") {
            //if (!statsReady || !tempCharacter.Race || !tempCharacter.Class) {
			if (!tempCharacter.Race || !tempCharacter.Class || !tempCharacter.Name || !tempCharacter.Alignment) {
				return;
			}
		}

		setActiveTab(tab);
	};

    // Event Handlers
    function onCanPointBuyAdjust(abilityName: string, delta: -1 | 1): boolean {
        if (isReadOnly) {
            return false;
        }
        return canAdjustPointBuy(tempCharacter.AbilityScores, abilityName, delta);
    }

    const onConfirmClass = (cls: IRpgClass) => {
        if (isReadOnly) {
            return;
        }

        setTempCharacter(prev => {
            const next = { ...prev };

            next.Class = cls.Name;
            next.HitDice = cls.HitDice;

            // Level 1 assumptions
            next.Level = 1;
            next.Xp = 0;

            // Derived stats
            RpgRules.calcHitPoints(next);
            RpgRules.calcArmorClass(next);
            RpgRules.calcInitive(next);
            RpgRules.setSkillValues(next);

            return next;
        });
    };

    function onPointBuyAdjust(abilityName: string, delta: -1 | 1): void {
        if (isReadOnly) {
            return;
        }

        if (!canAdjustPointBuy(tempCharacter.AbilityScores, abilityName, delta)) {
            return;
        }

        const updatedScores = tempCharacter.AbilityScores.map((stat) => {
            if (stat.Name !== abilityName) {
                return stat;
            }

            return {
                ...stat,
                Value: stat.Value + delta,
            };
        });

        setTempCharacter({
            ...tempCharacter,
            AbilityScores: updatedScores,
        });
    }

	const onRollAllScores = () => {
        if (isReadOnly) {
            return;
        }

        const rolledStats = RpgRules.generateStats(ABILITY_SCORES as unknown as string[]);

		setTempCharacter(prev => {
			const next = { ...prev };

			next.AbilityScores = RpgRules.setDefaultStats(rolledStats);
			RpgRules.calcArmorClass(next);

			return next;
		});
	};

    const onSelectBackground = (bg: IRpgBackground | null) => {
        if (isReadOnly) {
            return;
        }

        setSelectedBackground(bg);

        setTempCharacter(prev => {
            const next = { ...prev };
            RpgRules.applyBackground(next, bg);
            return next;
        });
    };

	const onResetPointBuy = () => {
        if (isReadOnly) {
            return;
        }

		setTempCharacter(prev => {
			const next = { ...prev };
			next.AbilityScores = RpgRules.setDefaultStats(next.AbilityScores).map(stat => {
				const value = 8;
				return {
					...stat,
					Value: value,
					Modifier: Math.floor((value + (stat.RacialModifier || 0) - 10) / 2),
				};
			});

			RpgRules.calcArmorClass(next);
			return next;
		});
	};

    const onSelectRace = (race: IRpgRace) => {
        if (isReadOnly) {
            return;
        }

        setSelectedRace(race);

        setTempCharacter(prev => {
            const next = { ...prev };

            next.Race = race.Name;
            next.Speed = race.Speed ? race.Speed : "";

            // Apply racial stat mods
            RpgRules.setStatModsForRace(next, race);

            // Recalculate derived stats
            RpgRules.calcArmorClass(next);

            return next;
        });
    };

	const onSaveCharacter = async () => {
        if (isReadOnly) {
            return;
        }

		// Final save: call your API to create/save the character.

        const saveChar: ISaveCharacter = {
            Id: tempCharacter.Id,
            Name: tempCharacter.Name,
            CharacterData: JSON.stringify(tempCharacter)
        };

        charAPI.saveChar(user.Id, saveChar)
        .then((ret: any) => {
            if (ret.message && (ret.message as string).includes("saved")) {
                toastService.success(ret.message);

                delCharFromStorage(charId);

                if (workshopMode === "create") {
                    navigate("/characters");
                    return;
                }

                if (workshopMode === "edit") {
                    setHasSaved(true);
                    return;
                }
            } else {
                if (ret === undefined || ret == null || ret === "") {
                    ret = "Save failed.";
                } else {
                    if (ret.message) {
                        toastService.error(ret.message);
                    } else {
                        toastService.error(ret);
                    }
                }
            }
        })
        .catch((err) => {
            toastService.error(err);
        });
	};

	return (
		<div className="char-content">
			<div className="char-text">
				<h2>Character Workshop - {workshopMode.charAt(0).toUpperCase() + workshopMode.slice(1)}</h2>

				{/* Tab buttons */}
                <div className="tabs" role="tablist" aria-label="Character Workshop Tabs">
                    {workshopTabs.map((tab) => {
                        return <button {...getTabButtonProps(tab)} key={tab}>{tab}</button> 
                    })}
				</div>

				{/* Tab content */}
                <div className="tab-panel">
                    {/* Abilities Tab */}
					{activeTab === "Abilities" && (
						<AbilitiesTab
                            isReadOnly={isReadOnly}
                            pointsRemaining={pointsRemaining}
                            statsReady={statsReady}
                            abilityScores={tempCharacter.AbilityScores}
                            portraitProps={buildPortraitProps()}
                            onRollAllScores={onRollAllScores}
                            onResetPointBuy={onResetPointBuy}

                            onPointBuyAdjust={onPointBuyAdjust}
                            canPointBuyAdjust={onCanPointBuyAdjust}
                            getPointBuyCostForScore={getPointBuyCost}
                        />
					)}

                    {/* Race Tab */}
					{activeTab === "Race" && (
                        <RaceTab
                            isReadOnly={isReadOnly}
                            raceList={raceList}
                            selectedRace={selectedRace}
                            gender={tempCharacter.Appearance?.Gender ?? "male"}
                            onSelectRace={onSelectRace}
                        />
                    )}

                    {/* Class Tab */}
                    {activeTab === "Class" && (
                        <ClassTab
                            isReadOnly={isReadOnly}
                            classList={classList}
                            selectedClass={selectedClass}
                            tempCharacter={tempCharacter}
                            setSelectedClass={setSelectedClass}
                            onConfirmClass={onConfirmClass}
                        />
                    )}

                    {/* Background Tab */}
                    {activeTab === "Background" && (
                        <BackgroundTab
                            isReadOnly={isReadOnly}
                            tempCharacter={tempCharacter}
                            setTempCharacter={setTempCharacter}
                            backgroundList={backgroundList}
                            selectedBackground={selectedBackground}
                            onSelectBackground={onSelectBackground}
                            alignmentList={alignmentList}
                        />
                    )}

                    {/* Review Tab */}
                    {activeTab === "Review" && (
                        <ReviewTab
                            isReadOnly={isReadOnly}
                            tempCharacter={tempCharacter}
                            reviewIssues={reviewIssues}
                            canSave={canSave}
                            mode={workshopMode} 
                            hasSaved={hasSaved}
                            selectedRace={selectedRace}
                            selectedClass={selectedClass}
                            onSaveCharacter={onSaveCharacter} 
                        />
                    )}

				</div>
			</div>
		</div>
	);
};

export default CharacterWorkshop;
